import { useMemo, useEffect, useState } from 'react';
import { Truck, FileText, ChevronDown, ChevronRight, FileUp } from 'lucide-react';
import { ASNScheduleDetail } from '../asnModalTypes';
import { ASNFormData, DeliveryLineOption, SourceType } from './types';

interface SourceLinesStepProps {
  sourceType: SourceType;
  onSourceChange: (s: SourceType) => void;
  formData: ASNFormData;
  onToggleSchedule: (id: number) => void;
  onToggleDeliveryLine: (id: number) => void;
  onSelectAllInGroup: (lines: DeliveryLineOption[]) => void;
  onOpenExcelImport: () => void;
  schedules: ASNScheduleDetail[];
  deliveryLines: DeliveryLineOption[];
  loadingSchedules: boolean;
  loadingDeliveryLines: boolean;
}

export function SourceLinesStep({
  sourceType,
  onSourceChange,
  formData,
  onToggleSchedule,
  onToggleDeliveryLine,
  onSelectAllInGroup,
  onOpenExcelImport,
  schedules,
  deliveryLines,
  loadingSchedules,
  loadingDeliveryLines,
}: SourceLinesStepProps) {
  const [expandedCallOffs, setExpandedCallOffs] = useState<Set<string>>(new Set());

  const groupedDeliveryLines = useMemo(() => {
    const groups = new Map<string, DeliveryLineOption[]>();
    for (const dl of deliveryLines) {
      const ref = dl.call_off_reference || 'Unknown call-off';
      if (!groups.has(ref)) groups.set(ref, []);
      groups.get(ref)!.push(dl);
    }
    return Array.from(groups.entries());
  }, [deliveryLines]);

  useEffect(() => {
    setExpandedCallOffs(new Set());
  }, [deliveryLines]);

  const toggleCallOffExpand = (ref: string) => {
    setExpandedCallOffs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">ASN Number</label>
        <div className="w-full px-3 py-2 border rounded-lg bg-muted/40 text-sm text-muted-foreground">
          Auto-generated on save (e.g. ASN000001)
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Source</label>
        <div className="flex rounded-lg border overflow-hidden">
          <button
            type="button"
            onClick={() => onSourceChange('schedules')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              sourceType === 'schedules'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-4 w-4" />
            PO Line Schedules
          </button>
          <button
            type="button"
            onClick={() => onSourceChange('delivery_lines')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              sourceType === 'delivery_lines'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Truck className="h-4 w-4" />
            Call-Off Delivery Lines
          </button>
          <button
            type="button"
            onClick={onOpenExcelImport}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 border-l"
          >
            <FileUp className="h-4 w-4" />
            Import from Excel
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Excel import creates new delivery lines from your ERP data and adds them to this ASN.
        </p>
      </div>

      {sourceType === 'schedules' && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Select PO Line Schedules <span className="text-red-500">*</span>
          </label>
          {loadingSchedules ? (
            <div className="text-sm text-muted-foreground">Loading schedules...</div>
          ) : schedules.length === 0 ? (
            <div className="text-sm text-muted-foreground">No available schedules found.</div>
          ) : (
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {schedules.map((schedule) => (
                <label
                  key={schedule.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={formData.schedules.includes(schedule.id)}
                    onChange={() => onToggleSchedule(schedule.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">
                      {schedule.po_line_details?.po_details?.po_number} - Line {schedule.po_line_details?.line_number}
                    </div>
                    <div className="text-muted-foreground">
                      Part: {schedule.po_line_details?.part_number} | Qty: {schedule.quantity} |
                      Due: {schedule.due_date ? new Date(schedule.due_date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {sourceType === 'delivery_lines' && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Select Delivery Lines <span className="text-red-500">*</span>
          </label>
          {loadingDeliveryLines ? (
            <div className="text-sm text-muted-foreground">Loading delivery lines...</div>
          ) : deliveryLines.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No available delivery lines found. Delivery lines are created when a call-off is confirmed.
            </div>
          ) : (
            <div className="border rounded-lg max-h-80 overflow-y-auto">
              {groupedDeliveryLines.map(([callOffRef, lines]) => {
                const isExpanded = expandedCallOffs.has(callOffRef);
                const lineIds = lines.map((l) => l.id);
                const selectedCount = lineIds.filter((id) =>
                  formData.delivery_line_ids.includes(id)
                ).length;
                const allSelected = selectedCount === lineIds.length && lineIds.length > 0;
                const someSelected = selectedCount > 0 && !allSelected;
                return (
                  <div key={callOffRef} className="border-b last:border-b-0">
                    <div className="flex items-center gap-2 p-3 bg-muted/30 hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={() => onSelectAllInGroup(lines)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4"
                      />
                      <button
                        type="button"
                        onClick={() => toggleCallOffExpand(callOffRef)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="font-medium text-sm">{callOffRef}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {selectedCount > 0 && (
                            <span className="text-blue-600 font-medium">{selectedCount} / </span>
                          )}
                          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                        </span>
                      </button>
                    </div>
                    {isExpanded &&
                      lines.map((dl) => (
                        <label
                          key={dl.id}
                          className="flex items-center gap-3 p-2 pl-10 hover:bg-muted/30 cursor-pointer border-t"
                        >
                          <input
                            type="checkbox"
                            checked={formData.delivery_line_ids.includes(dl.id)}
                            onChange={() => onToggleDeliveryLine(dl.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-xs">
                            <div className="font-medium">{dl.delivery_number}</div>
                            <div className="text-muted-foreground">
                              Part: {dl.part_number} | Qty: {dl.qty_released}
                              {dl.po_order_number && (
                                <> | PO: {dl.po_order_number}{dl.po_line_number != null && <> Line {dl.po_line_number}</>}</>
                              )}
                              {dl.delivery_option === 'direct' && ' | Direct'}
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
